/**
 * @fileoverview home-blocks.ts — the Projects-home block model.
 * @module apps/network/consumer/src/home/home-blocks
 *
 * The home is a stack of self-contained blocks rendered from an ordered
 * config. New surfaces (a hero image, a background video, a calendar embed,
 * another feed) are added by extending `HomeBlockKind` + dropping a component
 * in the registry — never a rewrite. Later, a per-user layout simply replaces
 * `DEFAULT_HOME_LAYOUT` with a persisted array; the canvas already consumes a
 * config, which is the whole point.
 */

/** A block kind — one entry in the `kind → component` registry (HomeCanvas). */
export type HomeBlockKind =
  | "hero"
  | "stat-strip"
  | "project-carousel"
  | "activity-feed"
  | "schedule"
  | "project-grid";

/** Grid span: full row, or the main/side pair of a 2-column region. */
export type HomeSpan = "full" | "main" | "side";

export interface HomeBlockSpec {
  /** Stable id (keying + future per-user reorder). */
  readonly id: string;
  readonly kind: HomeBlockKind;
  readonly span: HomeSpan;
  /** Block-specific config (e.g. reserved `media` for the hero). */
  readonly config?: Readonly<Record<string, unknown>>;
}

/** Columns each span occupies in the 12-col canvas grid. */
export const SPAN_COLS: Readonly<Record<HomeSpan, number>> = {
  full: 12,
  main: 8,
  side: 4,
};

/** The opinionated v1 landing: hero → stats → carousel → [feed | schedule] → grid. */
export const DEFAULT_HOME_LAYOUT: readonly HomeBlockSpec[] = [
  { id: "hero", kind: "hero", span: "full" },
  { id: "stats", kind: "stat-strip", span: "full" },
  { id: "carousel", kind: "project-carousel", span: "full" },
  { id: "feed", kind: "activity-feed", span: "main" },
  { id: "schedule", kind: "schedule", span: "side" },
  { id: "grid", kind: "project-grid", span: "full" },
];
